import { ActionSuccessResult, ActionContract, ActionRegistry } from "./registry-contract";
import { ActionImplementation, ActionDisplayLoc, HostedActionContent } from "./implementation-contract";
import { ActionTriggerLink, ActionTriggerOptions, RightRequirement } from "./trigger-contract";
import { LoggingService } from "../loggingSvc";
import { SdServicesSharedEnums } from "../sdServices-shared-enums";
import { AuthorizationService } from "../authorization/authorization";
import { LocalizationService } from "../../../ShellCommon/localization/localization";
import { DialogService } from "../dialog";

export const routeDiscoveryAttribute = "data-agent-action-route-target";
export const defaultRouteTarget = "sdSystem.defaultAgentActionRouteHost";

// TODO: One-to-many trigger -> implementations
// TODO: Opt-in validators for ActionContracts, specifically valuable for the above (otherwise implementations just manage it).
export class ActionRegistryImplementation implements ActionRegistry {
  private registeredActions = new Map<string, Set<ActionImplementation<any>>>();
  // @ngInject
  constructor(
    private $q: ng.IQService,
    private $state: ng.ui.IStateService,
    private dialogService: DialogService,
    private loggingService: LoggingService,
    private authorizationService: AuthorizationService,
    private sdLocalizationService: LocalizationService,
    private sdServicesSharedEnums: SdServicesSharedEnums
  ) {}

  public registerAction<TActionContract>(metadata: ActionImplementation<TActionContract>) {
    let actionList = this.registeredActions.get(metadata.triggeredBy);
    if (!actionList) {
      actionList = new Set<ActionImplementation<any>>();
      this.registeredActions.set(metadata.triggeredBy, actionList);
    }

    actionList.add(metadata);
  }

  public removeRegisteredAction<TActionContract>(triggeredBy: string) {
    this.registeredActions.delete(triggeredBy);
  }
  public invokeImplementation(actionName: string, actionContext: any): ng.IPromise<ActionSuccessResult> {
    return this.getActionTriggerLink(
      {
        actionContext: actionContext,
        name: actionName,
        restrictedUXTypes: [this.sdServicesSharedEnums.actionUXType.None]
      },
      undefined
    ).invoke();
  }

  public getMultiActionTriggerLinks(option: ActionTriggerOptions, element: JQuery): ActionTriggerLink[] {
    let actionList = this.registeredActions.get(option.name);
    if (actionList) {
      let actionTriggerLinks: ActionTriggerLink[] = [];
      actionList.forEach((action: ActionImplementation<any>) => {
        let actionTriggerLink = this.getActionTriggerLinkInternal(option, action, element);
        if (action.displayName) {
          actionTriggerLink.displayName = this.sdLocalizationService.getLocalizedString(
            action.displayName.locAppId,
            action.displayName.locKey
          );
          actionTriggerLink.automationId = action.automationId ? action.automationId : action.displayName.locKey;
          actionTriggerLinks.push(actionTriggerLink);
        }
      });

      return actionTriggerLinks;
    } else {
      this.loggingService.logError(`There are no actions found registered against trigger name as ${option.name}`);
      return undefined;
    }
  }

  public getActionTriggerLink(option: ActionTriggerOptions, element: JQuery): ActionTriggerLink {
    let actionList = this.registeredActions.get(option.name);
    if (!actionList) {
      throw new Error(
        "getActionTriggerLink has been invoked with no actions. Register an action with this trigger name."
      );
    }
    if (actionList.size > 1) {
      throw new Error(
        "getActionTriggerLink has been invoked with multiple actions. Either register one action per trigger or use getMultiActionTriggerLink()"
      );
    }

    return this.getActionTriggerLinkInternal(option, actionList.values().next().value, element);
  }

  private getActionTriggerLinkInternal(
    option: ActionTriggerOptions,
    action: ActionImplementation<any>,
    element: JQuery
  ): ActionTriggerLink {
    if (!action) {
      this.loggingService.logWarning(
        `Action with name ${option.name} could not be found! The action trigger will be disabled.`
      );
      return {
        getActionEligibility: () => {
          return this.sdServicesSharedEnums.actionEligibility.NotAllowed;
        },
        invoke: () => {
          return undefined;
        }
      };
    }

    let getActionContext = () => {
      if (angular.isFunction(option.actionContext)) {
        return option.actionContext();
      } else {
        return option.actionContext;
      }
    };

    let hasRights = true;
    let requiredRights: RightRequirement[] = [];
    if (option.requiredRights) {
      requiredRights = requiredRights.concat(option.requiredRights);
    }

    if (action.requiredRights) {
      requiredRights = requiredRights.concat(action.requiredRights);
    }

    hasRights = requiredRights.reduce((accumulator: boolean, rightRequest: RightRequirement) => {
      return accumulator && this.authorizationService.hasRight(rightRequest.rightName, rightRequest.appId);
    }, hasRights);

    return {
      getActionEligibility: () => {
        return this.getActionEligibility(option, action, getActionContext(), hasRights);
      },
      invoke: () => {
        let actionContext = getActionContext();
        if (
          this.getActionEligibility(option, action, actionContext, hasRights) !==
          this.sdServicesSharedEnums.actionEligibility.Default
        ) {
          return this.$q.reject("An attempt was made to run an ineligible action.");
        }

        let actionDeferral = this.$q.defer();

        this.log(this.sdServicesSharedEnums.logType.Info, option.name, "beginning");
        let hostingInstructions = action.invoke({
          actionContext,
          actionDeferral,
          showProgress: option.showProgress
        });
        let hostingType = this.getFirstAvailableUXType(option.restrictedUXTypes, action.uxTypes);
        if (hostingInstructions) {
          switch (hostingType) {
            case this.sdServicesSharedEnums.actionUXType.Modal:
              this.processModalContent(actionContext, actionDeferral, hostingInstructions, element);
              break;
            case this.sdServicesSharedEnums.actionUXType.Routed:
              this.processRoutedContent(
                actionContext,
                actionDeferral,
                hostingInstructions,
                element,
                action.displayName
              );
              break;
            case this.sdServicesSharedEnums.actionUXType.None:
              break;
          }
        }

        return actionDeferral.promise.then(
          (success) => {
            this.log(this.sdServicesSharedEnums.logType.Info, option.name, "completed", success);
            return success;
          },
          (error) => {
            this.log(this.sdServicesSharedEnums.logType.Error, option.name, "failed", error);
            return this.$q.reject(error);
          }
        );
      }
    };
  }

  private log(logType: any, actionName: string, actionState: string, actionStateContext?: any) {
    let logData = {
      isAgentAction: true,
      actionName: actionName,
      actionState: actionState,
      actionStateContext: actionStateContext
    };

    if (logType == this.sdServicesSharedEnums.logType.Info) {
      this.loggingService.logInfo(logData);
    }

    if (logType == this.sdServicesSharedEnums.logType.Error) {
      this.loggingService.logError(logData);
    }
  }

  private processRoutedContent(
    actionContext: any,
    actionDeferral: ng.IDeferred<any>,
    hostingInstructions: HostedActionContent,
    element: JQuery,
    displayName: ActionDisplayLoc
  ) {
    let currentState = this.$state.current;
    this.$state.go(this.locateRouteTarget(element), {
      actionContract: {
        actionContext: actionContext,
        actionDeferral: actionDeferral
      },
      content: hostingInstructions.content,
      displayName: displayName
    });
    actionDeferral.promise.finally(() => {
      this.$state.go(currentState.name, currentState.params);
    });
  }

  private processModalContent(
    actionContext: any,
    actionDeferral: ng.IDeferred<any>,
    hostingInstructions: HostedActionContent,
    element: JQuery
  ) {
    let actionData = {
      actionContext: actionContext,
      actionDeferral: actionDeferral,
      isActionHost: true
    };
    this.dialogService.showContent(hostingInstructions.content, "", actionData, true);
    actionDeferral.promise.finally(() => {
      this.dialogService.hide();
    });
  }

  private getActionEligibility(
    option: ActionTriggerOptions,
    action: ActionImplementation<any>,
    actionContext: any,
    isAuthorized: boolean
  ): any {
    if (this.getFirstAvailableUXType(option.restrictedUXTypes, action.uxTypes) == undefined) {
      return this.sdServicesSharedEnums.actionEligibility.NotApplicable;
    }

    let calculatedEligibility = action.getEligibility(actionContext);
    if (calculatedEligibility !== this.sdServicesSharedEnums.actionEligibility.NotApplicable && !isAuthorized) {
      // If the action is not applicable AND not authorized, we don't want to show NotAllowed (we'd rather show NotApplicable).
      calculatedEligibility = this.sdServicesSharedEnums.actionEligibility.NotAllowed;
    }

    return calculatedEligibility;
  }

  private getFirstAvailableUXType(optionUXTypes: any[], ActionUXTypes: any[]) {
    if (optionUXTypes && ActionUXTypes) {
      for (let i = 0; i < ActionUXTypes.length; i++) {
        if (optionUXTypes.indexOf(ActionUXTypes[i]) >= 0) {
          return ActionUXTypes[i];
        }
      }

      return undefined;
    }

    return ActionUXTypes[0];
  }

  private locateRouteTarget(element: JQuery): string {
    let closestRouteTarget = element.closest(`[${routeDiscoveryAttribute}]`);
    if (closestRouteTarget && closestRouteTarget.length > 0) {
      return closestRouteTarget.attr(routeDiscoveryAttribute);
    }
    return defaultRouteTarget;
  }
}
